import { useCallback, useContext, useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { DocumentUpload, Profile2User, SearchNormal1, ShieldTick } from "iconsax-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ReactComponent as DeleteIconDark } from "../../asset/icons/deleteIconDark.svg";
import { ReactComponent as EditIonDark } from "../../asset/icons/editIconDark.svg";
import { ToastContext } from "../../component/global/ToastContext";
import DBButton from "../../component/global/DBButton";
import DBPagination from "../../component/global/DBPagination";
import {
    COMPANY_CREATE_MEMBER,
    COMPANY_MEMBERS,
    companyMemberRemoveUrl,
    companyMemberSetAdminUrl,
    companyMemberUpdateUrl,
} from "../../helperData/ApiAddress";
import { patch, post } from "../../controlers/global/ConfigRequest";
import OrgUsersBulkModal from "./OrgUsersBulkModal";
import ConfirmModal from "./ConfirmModal";

function formatPhone(value) {
    if (!value) return "—";
    return String(value);
}

function memberDisplayName(member) {
    const full = `${member.first_name || ""} ${member.last_name || ""}`.trim();
    return full || formatPhone(member.phone_number);
}

function fieldError(error, key) {
    const data = error?.response?.data;
    if (!data) return null;
    const value = data[key];
    if (Array.isArray(value)) return value[0];
    if (typeof value === "string") return value;
    if (typeof data.detail === "string") return data.detail;
    return null;
}

function phoneKey(value) {
    const digits = String(value || "").replace(/\D/g, "");
    if (digits.length >= 10) return digits.slice(-10);
    return digits;
}

export default function OrgUsers({ form, auth }) {
    const { t } = useTranslation();
    const toast = useContext(ToastContext);
    const companyId = form?.id || auth?.usercompany;

    const [members, setMembers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [listLoading, setListLoading] = useState(false);
    const [btnLoader, setBtnLoader] = useState(false);
    const [adminLoadingId, setAdminLoadingId] = useState(null);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [pendingAdminChange, setPendingAdminChange] = useState(null);
    const [editingMember, setEditingMember] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleteLoadingId, setDeleteLoadingId] = useState(null);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [errors, setErrors] = useState({});
    const currentUserId = Number(
        auth?.id || sessionStorage.getItem("id") || 0
    );

    const authHeaders = () => ({
        Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
    });

    const loadMembers = useCallback(
        (page = 1, query = search) => {
            if (!companyId) return;
            setListLoading(true);
            axios
                .get(COMPANY_MEMBERS, {
                    headers: authHeaders(),
                    params: {
                        page,
                        search: query || undefined,
                        company: companyId,
                    },
                })
                .then((r) => {
                    const data = r.data;
                    if (Array.isArray(data)) {
                        setMembers(data);
                        setTotalCount(data.length);
                    } else {
                        setMembers(data?.results || []);
                        setTotalCount(data?.count || 0);
                    }
                })
                .catch(() => {
                    setMembers([]);
                    setTotalCount(0);
                    toast(
                        t("Organizational.Profile.OrgUsers.List.Error"),
                        "danger"
                    );
                })
                .finally(() => setListLoading(false));
        },
        [companyId, search, t, toast]
    );

    useEffect(() => {
        loadMembers(currentPage, search);
    }, [companyId, currentPage, search]); // eslint-disable-line react-hooks/exhaustive-deps

    const normalizePhone = (value) => {
        const digits = String(value || "").replace(/\D/g, "");
        if (digits.length >= 10) {
            return `+98${digits.slice(-10)}`;
        }
        return value.trim();
    };

    const focusFirstError = (nextErrors) => {
        const order = ["firstName", "lastName", "phone"];
        const first = order.find((key) => nextErrors[key]);
        if (!first) return;
        window.requestAnimationFrame(() => {
            document.getElementById(`org-user-${first}`)?.focus();
        });
    };

    const resetMemberForm = () => {
        setEditingMember(null);
        setFirstName("");
        setLastName("");
        setPhone("");
        setErrors({});
    };

    const startEdit = (member) => {
        setEditingMember(member);
        setFirstName(member.first_name || "");
        setLastName(member.last_name || "");
        setPhone(member.phone_number || "");
        setErrors({});
        window.requestAnimationFrame(() => {
            document.getElementById("org-user-firstName")?.focus();
        });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const nextErrors = {};
        if (!firstName.trim()) {
            nextErrors.firstName = t(
                "Organizational.Profile.OrgUsers.Error.FirstName"
            );
        }
        if (!lastName.trim()) {
            nextErrors.lastName = t(
                "Organizational.Profile.OrgUsers.Error.LastName"
            );
        }
        if (!phone.trim()) {
            nextErrors.phone = t(
                "Organizational.Profile.OrgUsers.Error.Phone"
            );
        } else {
            const duplicate = members.some(
                (member) =>
                    member.id !== editingMember?.id &&
                    phoneKey(member.phone_number) &&
                    phoneKey(member.phone_number) === phoneKey(phone)
            );
            if (duplicate) {
                nextErrors.phone = t(
                    "Organizational.Profile.OrgUsers.Error.Duplicate"
                );
            }
        }
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) {
            focusFirstError(nextErrors);
            return;
        }

        const payload = {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            phone_number: normalizePhone(phone),
            company: companyId,
        };
        const request = editingMember
            ? patch(companyMemberUpdateUrl(editingMember.id), payload)
            : post(COMPANY_CREATE_MEMBER, {
                  ...payload,
                  company_admin: false,
              });

        setBtnLoader(true);
        request
            .then(() => {
                toast(
                    t(
                        editingMember
                            ? "Organizational.Profile.OrgUsers.Edit.Success"
                            : "Organizational.Profile.OrgUsers.Create.Success"
                    ),
                    "success"
                );
                resetMemberForm();
                setCurrentPage(1);
                loadMembers(1, search);
            })
            .catch((e) => {
                const apiErrors = {
                    firstName: fieldError(e, "first_name"),
                    lastName: fieldError(e, "last_name"),
                    phone: fieldError(e, "phone_number"),
                };
                const cleaned = Object.fromEntries(
                    Object.entries(apiErrors).filter(([, v]) => v)
                );
                if (Object.keys(cleaned).length) {
                    setErrors(cleaned);
                    focusFirstError(cleaned);
                } else {
                    toast(
                        fieldError(e, "detail") ||
                            t(
                                editingMember
                                    ? "Organizational.Profile.OrgUsers.Edit.Error"
                                    : "Organizational.Profile.OrgUsers.Create.Error"
                            ),
                        "danger"
                    );
                }
            })
            .finally(() => setBtnLoader(false));
    };

    const handleDelete = (member) => {
        setDeleteLoadingId(member.id);
        axios
            .delete(companyMemberRemoveUrl(member.id), {
                headers: authHeaders(),
                params: { company: companyId },
            })
            .then(() => {
                toast(
                    t("Organizational.Profile.OrgUsers.Delete.Success"),
                    "success"
                );
                if (editingMember?.id === member.id) {
                    resetMemberForm();
                }
                loadMembers(currentPage, search);
            })
            .catch((e) =>
                toast(
                    fieldError(e, "detail") ||
                        t("Organizational.Profile.OrgUsers.Delete.Error"),
                    "danger"
                )
            )
            .finally(() => {
                setDeleteLoadingId(null);
                setPendingDelete(null);
            });
    };

    const handleToggleAdmin = (member, makeAdmin) => {
        setAdminLoadingId(member.id);
        axios
            .post(
                companyMemberSetAdminUrl(member.id),
                { company_admin: makeAdmin, company: companyId },
                { headers: authHeaders() }
            )
            .then((r) => {
                setMembers((prev) =>
                    prev.map((row) =>
                        row.id === member.id ? { ...row, ...r.data } : row
                    )
                );
                toast(
                    makeAdmin
                        ? t("Organizational.Profile.OrgUsers.Admin.On")
                        : t("Organizational.Profile.OrgUsers.Admin.Off"),
                    "success"
                );
            })
            .catch((e) =>
                toast(
                    fieldError(e, "detail") ||
                        t("Organizational.Profile.OrgUsers.Admin.Error"),
                    "danger"
                )
            )
            .finally(() => {
                setAdminLoadingId(null);
                setPendingAdminChange(null);
            });
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        setCurrentPage(1);
        setSearch(searchInput.trim());
    };

    return (
        <div className="page-load-fade org-users-tab">
            <div className="org-users-intro">
                <div className="org-users-intro-copy">
                    <h3 className="org-users-title text-pretty">
                        {t("Organizational.Profile.OrgUsers.Title")}
                    </h3>
                    <p className="org-users-subtitle">
                        {t("Organizational.Profile.OrgUsers.Subtitle")}
                    </p>
                </div>
                <DBButton
                    type="secondary"
                    outlined
                    className="org-users-bulk-trigger"
                    caption={
                        <>
                            <DocumentUpload size={18} aria-hidden="true" />{" "}
                            {t("Organizational.Profile.OrgUsers.Bulk.Open")}
                        </>
                    }
                    onClick={() => setBulkOpen(true)}
                />
            </div>

            <form
                className="general-info-form org-users-form"
                onSubmit={handleSubmit}
                autoComplete="off"
            >
                <Form.Group controlId="org-user-firstName">
                    <Form.Label>
                        {t("Organizational.Profile.Name.Place.Holder")}
                    </Form.Label>
                    <Form.Control
                        type="text"
                        name="first_name"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={t(
                            "Organizational.Profile.OrgUsers.Placeholder.FirstName"
                        )}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className={errors.firstName ? "input-err" : ""}
                        aria-invalid={Boolean(errors.firstName)}
                    />
                    {errors.firstName ? (
                        <div className="err" role="alert">
                            {errors.firstName}
                        </div>
                    ) : null}
                </Form.Group>

                <Form.Group controlId="org-user-lastName">
                    <Form.Label>
                        {t("Organizational.Profile.LastName.Place.Holder")}
                    </Form.Label>
                    <Form.Control
                        type="text"
                        name="last_name"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={t(
                            "Organizational.Profile.OrgUsers.Placeholder.LastName"
                        )}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className={errors.lastName ? "input-err" : ""}
                        aria-invalid={Boolean(errors.lastName)}
                    />
                    {errors.lastName ? (
                        <div className="err" role="alert">
                            {errors.lastName}
                        </div>
                    ) : null}
                </Form.Group>

                <Form.Group controlId="org-user-phone">
                    <Form.Label>
                        {t("Organizational.Profile.OrgUsers.Phone.Label")}
                    </Form.Label>
                    <Form.Control
                        type="tel"
                        name="phone_number"
                        inputMode="tel"
                        autoComplete="tel"
                        spellCheck={false}
                        dir="ltr"
                        placeholder={t(
                            "Organizational.Profile.OrgUsers.Placeholder.Phone"
                        )}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className={errors.phone ? "input-err" : ""}
                        aria-invalid={Boolean(errors.phone)}
                    />
                    {errors.phone ? (
                        <div className="err" role="alert">
                            {errors.phone}
                        </div>
                    ) : null}
                </Form.Group>

                <div className="btn-group org-users-form-actions">
                    <button
                        type="submit"
                        className="blue"
                        disabled={btnLoader}
                    >
                        {btnLoader ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            <span>
                                {t(
                                    editingMember
                                        ? "Organizational.Profile.OrgUsers.Edit.Save"
                                        : "Organizational.Profile.OrgUsers.Create.Button"
                                )}
                            </span>
                        )}
                    </button>
                    {editingMember ? (
                        <button
                            type="button"
                            className="red"
                            disabled={btnLoader}
                            onClick={resetMemberForm}
                        >
                            {t("Organizational.Profile.Cancel.Button")}
                        </button>
                    ) : null}
                </div>
            </form>

            <form
                className="org-users-search"
                onSubmit={handleSearchSubmit}
                role="search"
            >
                <label htmlFor="org-users-search-input" className="visually-hidden">
                    {t("Organizational.Profile.OrgUsers.Search.Label")}
                </label>
                <div className="org-users-search-field">
                    <SearchNormal1 size={16} aria-hidden="true" />
                    <input
                        id="org-users-search-input"
                        type="search"
                        name="org_users_search"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={t(
                            "Organizational.Profile.OrgUsers.Search.Placeholder"
                        )}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                </div>
                <button
                    type="submit"
                    className="form_btn blue org-users-search-btn"
                >
                    {t("Organizational.Profile.OrgUsers.Search.Button")}
                </button>
            </form>

            <div className="table-container org-users-table-container">
                <table className="user-table org-users-table">
                    <thead>
                        <tr className="header-row">
                            <th>
                                {t("Organizational.Profile.Row.Label")}
                            </th>
                            <th>
                                {t("Organizational.Profile.User.Label")}
                            </th>
                            <th>
                                {t(
                                    "Organizational.Profile.OrgUsers.Phone.Label"
                                )}
                            </th>
                            <th>
                                {t(
                                    "Organizational.Profile.OrgUsers.Admin.Label"
                                )}
                            </th>
                            <th className="org-users-actions-cell">
                                {t(
                                    "Organizational.Profile.Function.Label"
                                )}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {listLoading ? (
                            <tr>
                                <td colSpan="5">
                                    <Spinner animation="border" />
                                </td>
                            </tr>
                        ) : members.length === 0 ? (
                            <tr>
                                <td colSpan="5">
                                    <div className="org-users-empty">
                                        <Profile2User
                                            size={28}
                                            aria-hidden="true"
                                        />
                                        <span>
                                            {t(
                                                "Organizational.Profile.OrgUsers.Empty"
                                            )}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            members.map((member, index) => (
                                <tr
                                    className={
                                        editingMember?.id === member.id
                                            ? "user-table-row is-editing"
                                            : "user-table-row"
                                    }
                                    key={member.id}
                                >
                                    <td>
                                        <div className="level tabular-nums">
                                            {(currentPage - 1) * 20 +
                                                index +
                                                1}
                                        </div>
                                    </td>
                                    <td className="user min-w-0">
                                        <div className="org-users-name-cell">
                                            <span className="org-users-name truncate">
                                                {memberDisplayName(member)}
                                            </span>
                                            {member.role ? (
                                                <span className="org-users-role truncate">
                                                    {member.role}
                                                </span>
                                            ) : null}
                                        </div>
                                    </td>
                                    <td>
                                        <span
                                            className="org-users-phone tabular-nums"
                                            dir="ltr"
                                            translate="no"
                                        >
                                            {formatPhone(member.phone_number)}
                                        </span>
                                    </td>
                                    <td>
                                        {member.company_admin ? (
                                            <span className="org-users-admin-badge">
                                                {t(
                                                    "Organizational.Profile.OrgUsers.Admin.Badge"
                                                )}
                                            </span>
                                        ) : (
                                            <span className="org-users-member-badge">
                                                {t(
                                                    "Organizational.Profile.OrgUsers.Member.Badge"
                                                )}
                                            </span>
                                        )}
                                    </td>
                                    <td className="org-users-actions-cell">
                                        <div className="org-users-row-actions">
                                            <button
                                                type="button"
                                                className={
                                                    editingMember?.id ===
                                                    member.id
                                                        ? "org-users-icon-btn is-active"
                                                        : "org-users-icon-btn"
                                                }
                                                aria-label={t(
                                                    "Organizational.Profile.OrgUsers.Edit.Action"
                                                )}
                                                disabled={
                                                    deleteLoadingId ===
                                                    member.id
                                                }
                                                onClick={() =>
                                                    startEdit(member)
                                                }
                                            >
                                                <EditIonDark aria-hidden="true" />
                                            </button>
                                            {Number(member.id) !==
                                            currentUserId ? (
                                                <button
                                                    type="button"
                                                    className="org-users-icon-btn"
                                                    aria-label={t(
                                                        "Organizational.Profile.OrgUsers.Delete.Action"
                                                    )}
                                                    disabled={
                                                        deleteLoadingId ===
                                                        member.id
                                                    }
                                                    onClick={() =>
                                                        setPendingDelete(
                                                            member
                                                        )
                                                    }
                                                >
                                                    {deleteLoadingId ===
                                                    member.id ? (
                                                        <Spinner
                                                            animation="border"
                                                            size="sm"
                                                        />
                                                    ) : (
                                                        <DeleteIconDark aria-hidden="true" />
                                                    )}
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                className={
                                                    member.company_admin
                                                        ? "org-users-icon-btn is-admin"
                                                        : "org-users-icon-btn"
                                                }
                                                aria-pressed={
                                                    member.company_admin
                                                }
                                                aria-label={
                                                    member.company_admin
                                                        ? t(
                                                              "Organizational.Profile.OrgUsers.Admin.Revoke"
                                                          )
                                                        : t(
                                                              "Organizational.Profile.OrgUsers.Admin.Grant"
                                                          )
                                                }
                                                disabled={
                                                    adminLoadingId ===
                                                    member.id
                                                }
                                                onClick={() =>
                                                    setPendingAdminChange({
                                                        member,
                                                        makeAdmin:
                                                            !member.company_admin,
                                                    })
                                                }
                                            >
                                                {adminLoadingId ===
                                                member.id ? (
                                                    <Spinner
                                                        animation="border"
                                                        size="sm"
                                                    />
                                                ) : (
                                                    <ShieldTick
                                                        size={18}
                                                        variant={
                                                            member.company_admin
                                                                ? "Bold"
                                                                : "Linear"
                                                        }
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                {totalCount > 20 ? (
                    <DBPagination
                        current={currentPage}
                        total={totalCount}
                        onChange={(page) => setCurrentPage(page)}
                    />
                ) : null}
            </div>

            {bulkOpen ? (
                <OrgUsersBulkModal
                    companyId={companyId}
                    onHide={() => setBulkOpen(false)}
                    onImported={() => {
                        setCurrentPage(1);
                        loadMembers(1, search);
                    }}
                />
            ) : null}
            <ConfirmModal
                show={Boolean(pendingAdminChange)}
                onHide={() => setPendingAdminChange(null)}
                onConfirm={() =>
                    handleToggleAdmin(
                        pendingAdminChange.member,
                        pendingAdminChange.makeAdmin
                    )
                }
                title={t(
                    pendingAdminChange?.makeAdmin
                        ? "incentives.admin.confirmGrant.title"
                        : "incentives.admin.confirmRevoke.title"
                )}
                description={t(
                    pendingAdminChange?.makeAdmin
                        ? "incentives.admin.confirmGrant.description"
                        : "incentives.admin.confirmRevoke.description",
                    {
                        name: pendingAdminChange
                            ? memberDisplayName(pendingAdminChange.member)
                            : "",
                    }
                )}
                btnTitle={t(
                    pendingAdminChange?.makeAdmin
                        ? "Organizational.Profile.OrgUsers.Admin.Grant"
                        : "Organizational.Profile.OrgUsers.Admin.Revoke"
                )}
            />
            <ConfirmModal
                show={Boolean(pendingDelete)}
                onHide={() => setPendingDelete(null)}
                onConfirm={() => handleDelete(pendingDelete)}
                title={t("Organizational.Profile.OrgUsers.Delete.Title")}
                description={t(
                    "Organizational.Profile.OrgUsers.Delete.Description",
                    {
                        name: pendingDelete
                            ? memberDisplayName(pendingDelete)
                            : "",
                    }
                )}
                btnTitle={t(
                    "Organizational.Profile.OrgUsers.Delete.Confirm"
                )}
            />
        </div>
    );
}
